import {Tabs} from 'antd'
import DescriptionList from 'src/components/elements/DescriptionList/DescriptionList'
import {ThreeColumnsGrid} from './TherapyGridStyled'
import {
  formatDate,
  formatDeviceOfAssumption,
  formatFrequencyDays,
  formatModeOfAssumption,
  formatTherapyEach,
  formatUnitOfMeasure
} from './therapyFormatters'

type TherapyCardTabsProps = {
  therapy: Record<string, any>
}

const renderCustomDosages = (dosages: Record<string, any>[] = []) => (
  <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px'}}>
    <span style={{color: '#A6A6A6', fontSize: '12px'}}>{'Dosaggio personalizzato'}</span>
    <ThreeColumnsGrid>
      {dosages.map((dosage, index) => (
        <div key={index}>
          <b>{`Assunzione ${index + 1}:`}</b>
          <div style={{display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 0px 8px 8px'}}>
            <b>{`Dosaggio: ${formatUnitOfMeasure(dosage)}`}</b>
            {dosage.infusionRate && <span>{`Velocità di infusione: ${formatUnitOfMeasure(dosage.infusionRate)}`}</span>}
            {dosage.secondaryDosage && <span>{`Dosaggio secondario: ${formatUnitOfMeasure(dosage.secondaryDosage)}`}</span>}
            {dosage.dilution && <span>{`Diluizione (ml): ${dosage.dilution}`}</span>}
            {dosage.calculationCriterion && <span>{`Criterio di calcolo: ${formatUnitOfMeasure(dosage.calculationCriterion)}`}</span>}
          </div>
        </div>
      ))}
    </ThreeColumnsGrid>
  </div>
)

const TherapyCardTabs: React.FC<TherapyCardTabsProps> = ({therapy}) => {
  const {
    assumptionDosage = {},
    calculationCriterion = {},
    customDosages,
    description = '',
    deviceOfAssumption = '',
    drugDosage = {},
    endDate,
    reminderEndDate,
    frequency = {},
    isContinued,
    modeOfAssumption = '',
    monthlyDosage = {},
    dailyDosage = {},
    vialDosage = {},
    notes = '',
    startDate,
    assumptionsStartDate,
    weeklyDosage = {},
    speedOfDevice = {},
    diagnosisDate,
    treatmentStartDate,
    homeTreatmentStartDate,
    preTreatment,
    preTreatmentNotes,
    postTreatment,
    postTreatmentNotes,
    emergencyTreatment,
    emergencyTreatmentNotes
  } = therapy

  return (
    <Tabs
      items={[
        {
          label: 'Generali',
          key: 'general',
          children: (
            <DescriptionList items={[
              {label: 'Data di inizio terapia', value: formatDate(startDate, 'DD MMMM YYYY')},
              {label: 'Data di inizio assunzioni', value: formatDate(assumptionsStartDate, 'DD MMMM YYYY')},
              {label: 'Data promemoria scadenza', value: formatDate(reminderEndDate, 'DD MMMM YYYY')},
              {label: 'Data diagnosi', value: formatDate(diagnosisDate, 'DD MMMM YYYY')},
              {label: 'Data inizio trattamento', value: formatDate(treatmentStartDate, 'DD MMMM YYYY')},
              {label: 'Data inizio trattamento domiciliare', value: formatDate(homeTreatmentStartDate, 'DD MMMM YYYY')},
              {label: 'Data di fine', value: formatDate(endDate, 'DD MMMM YYYY')},
              {label: 'Terapia continuativa', value: isContinued ? 'Sì' : 'No'},
              {label: 'Descrizione', value: description},
              {label: 'Pre-trattamento', value: preTreatment ? (preTreatmentNotes ?? 'Sì') : 'No'},
              {label: 'Post-trattamento', value: postTreatment ? (postTreatmentNotes ?? 'Sì') : 'No'},
              {label: 'Trattamento di emergenza', value: emergencyTreatment ? (emergencyTreatmentNotes ?? 'Sì') : 'No'},
              {label: 'Note', value: notes}
            ]}
            />
          )
        },
        ...!isContinued ? [{
          label: 'Frequenza',
          key: 'frequency',
          children: (
            <DescriptionList items={[
              {label: 'Frequenza assunzioni', value: `${frequency.times} ogni ${formatTherapyEach(frequency.each)}`},
              ...frequency.days?.length ? [{label: 'Da ripetere nei giorni', value: formatFrequencyDays(frequency.days)}] : [],
              {label: 'Giorni di tolleranza', value: formatUnitOfMeasure(frequency.toleranceDays)}
            ]}
            />
          )
        }] : [],
        {
          label: 'Dosaggio',
          key: 'dosage',
          children: (
            <>
              <DescriptionList items={[
                ...!customDosages ? [
                  {label: 'Dosaggio per assunzione', value: formatUnitOfMeasure(assumptionDosage)},
                  {label: 'Velocità infusionale', value: formatUnitOfMeasure(assumptionDosage.infusionRate)},
                  {label: 'Dosaggio secondario', value: formatUnitOfMeasure(assumptionDosage.secondaryDosage)},
                  {label: 'Diluizione (ml)', value: assumptionDosage.dilution}
                ] : [],
                {label: 'Dosaggio singolo', value: formatUnitOfMeasure(drugDosage)},
                {label: 'Dosaggio settimanale', value: formatUnitOfMeasure(weeklyDosage)},
                {label: 'Dosaggio mensile', value: formatUnitOfMeasure(monthlyDosage)},
                {label: 'Dosaggio giornaliero', value: formatUnitOfMeasure(dailyDosage)},
                {label: 'Dosaggio fiala', value: formatUnitOfMeasure(vialDosage)},
                {label: 'Criterio di calcolo', value: formatUnitOfMeasure(calculationCriterion)}
              ]}
              />
              {customDosages && renderCustomDosages(customDosages)}
            </>
          )
        },
        {
          label: 'Modalità di assunzione',
          key: 'administration',
          children: (
            <DescriptionList items={[
              {label: 'Via di assunzione', value: formatModeOfAssumption(modeOfAssumption)},
              {label: 'Dispositivo', value: formatDeviceOfAssumption(deviceOfAssumption)},
              {label: 'Velocità device', value: formatUnitOfMeasure(speedOfDevice)}
            ]}
            />
          )
        }
      ]}
      size='small'
      type='card'
    />
  )
}

export default TherapyCardTabs
